import {
  transformDonationOrderDetails,
  transformOrder,
  transformOrderOperation,
  transformOrdersList,
  transformReimbursementOrderDetails,
} from '@/common/integrations/menhir/employee/orders/orders.dto';
import {
  ICancelReimbursementOrderParams,
  ICreateDonationOrderParams,
  ICreateOrderParams,
  ICreatePolicyTopUpParams,
  ICreateReimbursementOrderParams,
  IDeleteOrderParams,
  IDonationOrder,
  IGetOrdersParams,
  IGetReimbursementDetailsParams,
  IMenhirDonationOrderEnvelope,
  IMenhirOrderEnvelope,
  IMenhirOrderOperation,
  IMenhirOrdersList,
  IMenhirReimbursementOrderDetailsEnvelope,
  IOrder,
  IReimbursementOrderDetails,
  IRetirementInvestmentOrder,
  IUpdateOrderParams,
  IUploadReimbursementFilesParams,
  TOrderCategorySlug,
} from '@/common/integrations/menhir/employee/orders/orders.types';
import { menhirClient } from '@/common/integrations/menhir/menhir.client';
import {
  MENHIR_ENDPOINTS,
  endpointFor,
} from '@/common/integrations/menhir/menhir.endpoints';
import { formatDateISO } from '@/common/utils/date';
import { getFormDataFromObject } from '@/common/utils/form';
import { ArrayUtils, TMoney } from '@coverflex-tech/hypernova-utils';
import { AxiosRequestConfig } from 'axios';

export const createCashoutOrder = (amount: TMoney) =>
  menhirClient
    .post<
      null,
      IMenhirOrderEnvelope<'cashout'>
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.CASHOUTS), { amount })
    .then((envelope) => transformOrder(envelope.order));

export const getOrders = <
  T extends TOrderCategorySlug,
  U extends IOrder<T> = IOrder<T>,
>({
  category,
}: IGetOrdersParams<T>) =>
  menhirClient
    .get<null, IMenhirOrdersList<T>>(
      endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.ROOT, category),
      {
        // TODO: Re-enable this after menhir is fixed
        // params: {
        //   pagination: 'no',
        // },
      }
    )
    .then((envelope) => transformOrdersList<T, U>(envelope.orders?.list ?? []));

export const getOrder = <
  T extends TOrderCategorySlug,
  U extends IOrder<T> | IRetirementInvestmentOrder = IOrder<T>,
>(
  category: TOrderCategorySlug,
  orderId: string
): Promise<U> =>
  menhirClient
    .get<
      null,
      IMenhirOrderEnvelope<T>
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.DETAIL, category, orderId))
    .then((envelope) => transformOrder(envelope.order) as U);

export const createOrder = <T extends TOrderCategorySlug>(
  { category, productId, amount, recurrent }: ICreateOrderParams<T>,
  config?: AxiosRequestConfig
): Promise<string> =>
  menhirClient
    .post<null, IMenhirOrderEnvelope<T>>(
      endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.ROOT, category),
      {
        product_id: productId,
        amount: {
          amount: amount.amount,
          currency: amount.currency,
        },
        has_recurrence: recurrent,
      },
      config
    )
    .then((envelope) => envelope.order.id);

export const updateOrder = <T extends TOrderCategorySlug>({
  category,
  orderId,
  amount,
  productId,
  recurrent,
}: IUpdateOrderParams<T>) =>
  menhirClient.patch(
    endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.DETAIL, category, orderId),
    {
      amount,
      product_id: productId,
      has_recurrence: recurrent,
    }
  );

export const deleteOrder = <T extends TOrderCategorySlug>({
  category,
  orderId,
}: IDeleteOrderParams<T>) =>
  menhirClient.delete(
    endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.DETAIL, category, orderId)
  );

export const createPolicyTopUp = <T extends TOrderCategorySlug>(
  {
    category,
    policyId,
    amount,
    confirmInvestmentTopUp,
  }: ICreatePolicyTopUpParams<T>,
  config?: AxiosRequestConfig
) =>
  menhirClient
    .post<null, { operation: IMenhirOrderOperation }>(
      endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.TOP_UP, category),
      {
        policy_id: policyId,
        amount: {
          amount: amount.amount,
          currency: amount.currency,
        },
        confirm_investment_topup: confirmInvestmentTopUp,
      },
      config
    )
    .then((envelope) => transformOrderOperation(envelope.operation));

// DONATIONS
export const getDonationOrderDetail = (
  orderId: string
): Promise<IDonationOrder> =>
  menhirClient
    .get<
      null,
      IMenhirDonationOrderEnvelope
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.DONATIONS.DETAIL, orderId))
    .then(transformDonationOrderDetails);

export const createDonationOrder = ({
  establishmentId,
  amount,
}: ICreateDonationOrderParams) =>
  menhirClient
    .post<null, IMenhirDonationOrderEnvelope>(
      MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.DONATIONS.ROOT,
      {
        establishment_id: establishmentId,
        amount: amount.amount,
      }
    )
    .then(transformDonationOrderDetails);

// REIMBURSEMENTS
export const getReimbursementOrderDetails = <T extends TOrderCategorySlug>({
  category,
  orderId,
}: IGetReimbursementDetailsParams<T>): Promise<IReimbursementOrderDetails<T>> =>
  menhirClient
    .get<
      null,
      IMenhirReimbursementOrderDetailsEnvelope<T>
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.REIMBURSEMENTS.DETAIL, category, orderId))
    .then((envelope) => transformReimbursementOrderDetails(envelope.order));

export const uploadReimbursementFiles = async <T extends TOrderCategorySlug>({
  category,
  files,
}: IUploadReimbursementFilesParams<T>) => {
  const fileData = getFormDataFromObject({
    files: files.map((file) => file.blob),
  });

  return menhirClient
    .post<
      null,
      { order_id: string }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.REIMBURSEMENTS.FILES, category), fileData)
    .then((envelope) => ({ orderId: envelope.order_id }));
};

export const createReimbursementOrder = async <T extends TOrderCategorySlug>(
  reimbursementOrderData:
    | ICreateReimbursementOrderParams<T>
    | (Omit<ICreateReimbursementOrderParams<T>, 'files'> & {
        orderId: string;
      }),
  config?: AxiosRequestConfig
): Promise<string> => {
  const {
    category,
    amount,
    vatAmount,
    purchasedAt,
    reimbursableItem,
    budgetEmployeeId,
    description,
    invoiceNumber,
    fiscalYear,
    beneficiaryPersonId,
    education,
    confirmReimbursementCreation,
    customFields,
    supportFiles,
    asFormData,
  } = reimbursementOrderData;

  let orderId: string;

  if ('files' in reimbursementOrderData) {
    const { files } = reimbursementOrderData;
    const { orderId: newOrderId } = await uploadReimbursementFiles({
      category,
      files,
    });
    orderId = newOrderId;
  } else {
    orderId = reimbursementOrderData.orderId;
  }

  const orderData = Object.fromEntries(
    Object.entries({
      amount,
      purchased_at: formatDateISO(purchasedAt),
      description,
      vat_amount: vatAmount,
      reimbursable_item_id:
        category === 'retirement-investment' && reimbursableItem
          ? reimbursableItem.id
          : undefined,
      budget_employee_id: budgetEmployeeId ?? undefined,
      invoice_number: invoiceNumber ?? undefined,
      fiscal_year: fiscalYear ?? undefined,
      beneficiary_person_id: beneficiaryPersonId ?? undefined,
      education_level: education ?? undefined,
      id: orderId,
      confirm_reimbursement_creation: confirmReimbursementCreation,
      custom_fields: customFields ?? undefined,
      support_files: supportFiles
        ?.map((file) => file.blob)
        .filter(ArrayUtils.removeFalsey),
    }).filter(([_, value]) => value !== undefined && value !== null)
  );

  return menhirClient
    .put<
      null,
      IMenhirOrderEnvelope<T>
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.REIMBURSEMENTS.DETAIL, category, orderId), asFormData ? getFormDataFromObject(orderData) : orderData, config)
    .then((envelope) => envelope.order.id);
};

export const cancelReimbursementOrder = <T extends TOrderCategorySlug>({
  category,
  orderId,
}: ICancelReimbursementOrderParams<T>) =>
  menhirClient.post(
    endpointFor(
      MENHIR_ENDPOINTS.EMPLOYEE.ORDERS.REIMBURSEMENTS.CANCEL,
      category,
      orderId
    )
  );
